// src/routes/AppRoutes.tsx
import React, { Suspense, useEffect } from "react";
import { Routes, Route, useLocation, useNavigationType, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
//Components Import
import ProtectedRoute from "../Auth/ProtectedRoute";
import AdminProtectedRoute from "../Auth/AdminProtectedRoute";
import ErrorBoundary from "../components/ErrorBoundary";
import Loader from "../components/loader/Loader";
import Header from "../components/header/Header";
import KitchenDashboard from "../pages/KitchenDashboard";
import SiteManagerDashboard from "../pages/SiteManagerDashboard";
import ProductionPlan from "../pages/ProductionPlan";
import ExceptionalMenuRequisition from "../pages/ExceptionalMenuRequisition";
import FinalKitchenGIVarianceReport from "../pages/VarianceReport/FinalKitchenGIVarianceReport";
import KitchenIndentVarianceReport from "../pages/VarianceReport/KitchenIndentVarianceReport";
import QuantitiesVarianceReport from "../pages/VarianceReport/QuantitiesVarianceReport";
import UpdateProductionPlan from "../pages/UpdateProductionPlan";
import KitchenIndent from "../pages/KitchenIndent";
import SpecialSite from "../pages/SpecialSite";
import HolidayCalendar from "../pages/HolidayCalendar";
import AdminConfiguration from "../pages/AdminConfiguration";
import Footer from "../components/Footer";
import UserConfiguration from "../pages/RBACConfigurationManagement/UserConfiguration";
import EditUserConfiguration from "../pages/RBACConfigurationManagement/EditUserConfiguration";
import RoleConfiguration from "../pages/RBACConfigurationManagement/RoleConfiguration";
import EditRoleConfiguration from "../pages/RBACConfigurationManagement/EditRoleConfiguration";

import ProductionPlanReport from "../pages/ProductionPlanReport";
// Lazy loading the pages
const Dashboard = React.lazy(() => import("../pages/Dashboard"));
const InitiateMenuPlan = React.lazy(() => import("../pages/InitialMenuPlan"));
const IteamInitialMenuPlan = React.lazy(() => import("../pages/IteamInitialMenuPlan"));
const Login = React.lazy(() => import("../components/Login/Login"));
const Reports = React.lazy(() => import("../pages/Reports"));
const ItemReports = React.lazy(() => import("../pages/ItemReports"));
const CpuPlannedReports = React.lazy(() => import("../pages/CpuPlannedReports"));
const MenuProjection = React.lazy(() => import("../pages/MenuProjection"));
const ItemMenuProjection = React.lazy(() => import("../pages/ItemMenuProjection"));
const MenuProjectionSelection = React.lazy(() => import("../pages/MenuProjectionSelection"));
const ItemMenuProjectionSelection = React.lazy(() => import("../pages/ItemMenuProjectionSelection"));
const MenuRequisition = React.lazy(() => import("../pages/MenuRequisition"));
const ItemMenuRequisition = React.lazy(() => import("../pages/ItemMenuRequisition"));
const Error = React.lazy(() => import("../pages/Error"));
const ProjectedKitchenIndent = React.lazy(() => import("../pages/ProjectedKitchenIndent"));
const BridgerMapping = React.lazy(() => import("../pages/BridgerDetails"));
const MenuProjectionDashboard = React.lazy(() => import("../pages/MenuProjectionDashboard"));
const MapOtherDishes = React.lazy(() => import("../pages/MapOtherDishes"));

const AppRoutes = () => {
  const location = useLocation();
  const navigationType = useNavigationType(); // Can be 'push', 'replace', or 'pop'
  const navigate = useNavigate();

// Safely read `userInfo` from localStorage and normalize `isitemwise` to a boolean.
  let userIsItem = false;
  const rawUser = localStorage.getItem('userInfo');
    if (rawUser) {
      const parsed = JSON.parse(rawUser);
      const val = parsed?.userdetails?.isitemwise;    
      userIsItem = val === true;
    }
  // Show header when not on login and an auth token or userid exists  
  const showHeader = location.pathname !== "/" && (
    !!localStorage.getItem("token") ||
    !!localStorage.getItem("accessToken") ||
    !!localStorage.getItem("userid")
  );

  const storedModules = JSON.parse(localStorage.getItem('allmodules') ?? '[]');
  const moduleNames = storedModules?.allmodules?.flatMap((modulegroup: any) =>
    modulegroup.modules.map((mod: any) => '/' + mod.modulename.toLowerCase())
  ) ?? [];
 
  useEffect(() => {
   
    const allowedDirectPaths = ['/dashboard', '/error', '/report','/cpuplannedreport', '/mapotherdishes', '/', '', ...moduleNames];
    // Check for secret query param /anypath?dev=true
    const searchParams = new URLSearchParams(location.search);
    const hasBypass = searchParams.get('dev') === 'true';
    const Isadmin = localStorage.getItem('roleId') === '1';

    if (!Isadmin && navigationType === "POP" && !allowedDirectPaths.includes(location.pathname) && !hasBypass) {
      navigate('/', { replace: true });
      toast("You can't access this module directly, please navigate through app.", { type: "warning" });
    }
  }, [location, navigationType]);

  // When user returns to the tab (or focuses the window), ensure they still have a valid session;
  // if not, redirect to login so protected routes don't render blank UI.
  useEffect(() => {
    const ensureAuth = () => {
      const hasAuth = !!localStorage.getItem('token') || !!localStorage.getItem('accessToken') || !!localStorage.getItem('userid');
      if (!hasAuth && location.pathname !== '/') {
        navigate('/', { replace: true });
      }
    };

    const handleVisibility = () => {
      if (!document.hidden) ensureAuth();
    };

    window.addEventListener('focus', ensureAuth);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', ensureAuth);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [navigate, location]);


  return (
    <Suspense fallback={<Loader />}>
      <ErrorBoundary>
        {showHeader && <Header />}
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
          {/* Render different initial menu plan page based on selected item type */}
          <Route
            path="/initiatemenuplan"
            element={
              <ProtectedRoute
                element={
                  !userIsItem? (
                     <InitiateMenuPlan />
                 
                  ) : (
                    <IteamInitialMenuPlan />
                  )
                }
              />
            }
          />
            <Route
            path="/report"
            element={
              <ProtectedRoute
                element={
                  !userIsItem? (
                     <Reports />
                 
                  ) : (
                    <ItemReports />
                  )
                }
              />
            }
          />          
          {/* <Route path="/report" element={<ProtectedRoute element={<Reports />} />} /> */}

          <Route path="/cpuplannedreport" element={<ProtectedRoute element={<CpuPlannedReports />} />} />
           <Route
            path="/menuprojection"
            element={
              <ProtectedRoute
                element={
                  !userIsItem? (
                     <MenuProjection />
                 
                  ) : (
                    <ItemMenuProjection />
                  )
                }
              />
            }
          />  
          <Route
            path="/menuprojectionselection"
            element={
              <ProtectedRoute
                element={
                  !userIsItem? (
                     <MenuProjectionSelection />
                 
                  ) : (
                     <ItemMenuProjectionSelection />
                  )
                }
              />
            }
          />   
          <Route path="/menuprojectionselection" element={<ProtectedRoute element={<MenuProjectionSelection />} />} />
          <Route path="/BridgerMapping" element={<ProtectedRoute element={<BridgerMapping />} />} />
          <Route path="/menuprojectiondashboard" element={<ProtectedRoute element={<MenuProjectionDashboard />} />} />
          {/* <Route path="/menurequisition" element={<ProtectedRoute element={<MenuRequisition />} />} /> */}
          <Route path="/menurequisition" 
          element={<ProtectedRoute
           element={
                  !userIsItem? (
                     <MenuRequisition />
                 
                  ) : (
                    <ItemMenuRequisition />
                  )
                }
              />
            }
          />      
          <Route path="/projectedkitchenindent" element={<ProtectedRoute element={<ProjectedKitchenIndent />} />} />
          <Route path="/kitchendashboard" element={<ProtectedRoute element={<KitchenDashboard />} />} />
          <Route path="/sitemanagerdashboard" element={<ProtectedRoute element={<SiteManagerDashboard />} />} />
          <Route path="/productionplan" element={<ProtectedRoute element={<ProductionPlan />} />} />
          <Route path="/productionplanreport" element={<ProtectedRoute element={<ProductionPlanReport />} />} />
          <Route path="/previousproductionplan" element={<ProtectedRoute element={<ProductionPlan key="previousPlan" />} />} />
          <Route path="/exceptionalmenurequisition" element={<ProtectedRoute element={<ExceptionalMenuRequisition />} />} />
          <Route path="/error" element={<Error />} />
          <Route path="/kitchenindentvsactualindentreport" element={<ProtectedRoute element={<FinalKitchenGIVarianceReport />} />} />
          <Route path="/projectedkitchenindentvskitchenindentreport" element={<ProtectedRoute element={<KitchenIndentVarianceReport />} />} />
          <Route path="/menuprojectionvsmenurequisitionreport" element={<ProtectedRoute element={<QuantitiesVarianceReport />} />} />
          <Route path="/updateproductionplan" element={<ProtectedRoute element={<UpdateProductionPlan />} />} />
          <Route path="/kitchenindent" element={<ProtectedRoute element={<KitchenIndent />} />} />
          <Route path="/specialsite" element={<ProtectedRoute element={<SpecialSite />} />} />
          <Route path="/holidaycalendar" element={<ProtectedRoute element={<HolidayCalendar />} />} />
          <Route path="/adminconfiguration" element={<ProtectedRoute element={<AdminConfiguration />} />} />
          <Route path="/userconfiguration" element={<ProtectedRoute element={<UserConfiguration />} />} />
          <Route path="/userconfiguration/view/:id" element={<ProtectedRoute element={<EditUserConfiguration />} />} />
          <Route path="/userconfiguration/edit/:id" element={<ProtectedRoute element={<EditUserConfiguration />} />} />
          <Route path="/roleconfiguration" element={<ProtectedRoute element={<RoleConfiguration />} />} />
          <Route path="/roleconfiguration/mapmodule/:id" element={<ProtectedRoute element={<EditRoleConfiguration />} />} />
          <Route path="/mapotherdishes" element={<AdminProtectedRoute element={<MapOtherDishes />} />} />
        </Routes>
        {showHeader && <Footer />}
      </ErrorBoundary>
    </Suspense>
  );
};

export default AppRoutes;
